/**
 * Labstep
 *
 * @module components/ProtocolCollection/Action/CreateWithFolder
 * @desc Create a protocol collection by selecting a folder (and optional template)
 */

import { FieldType } from 'labstep-web/constants/form';
import { withActiveGroup } from 'labstep-web/containers/ActiveGroup';
import { EntityCanCreateContainer } from 'labstep-web/containers/Entity/Create/Can';
import { ActionComponentModalContainer as ActionComponent } from 'labstep-web/core/Action/Component';
import { FormReusable } from 'labstep-web/core/Form/Reusable';
import type { IFieldProps } from 'labstep-web/core/Form/Reusable/types';
import { ModalDefaultContainer as Modal } from 'labstep-web/core/Modal/Default';
import { Folder } from 'labstep-web/models/folder.model';
import { ProtocolCollection } from 'labstep-web/models/protocol-collection.model';
import { navigation } from 'labstep-web/services/navigation';
import type React from 'react';
import { withRouter } from 'react-router';
import { object } from 'yup';
import type { ProtocolCollectionActionCreateWithFolderProps } from './types';

export const ProtocolCollectionActionCreateWithFolder: React.FC<
  ProtocolCollectionActionCreateWithFolderProps
> = ({ actionComponentProps, activeGroup, history }) => (
  <Modal
    header="New Protocol"
    viewComponent={
      <ActionComponent
        type="button"
        text="New Protocol"
        {...actionComponentProps}
      />
    }
    usePortal
    content={({ toggleModal }) => (
      <EntityCanCreateContainer
        entityName={ProtocolCollection.entityName}
        showLoaderWhileCreating
      >
        {({ status, create }) => {
          const folderField: IFieldProps = {
            fieldType: FieldType.SearchSelect,
            name: 'folder',
            fieldLabel: Folder.getHumanReadableEntityName(
              false,
              true,
            ),
            validation: object().required(),
            elementProps: {
              entityName: Folder.entityName,
              params: {
                group_id: activeGroup?.id,
                type: ProtocolCollection.entityName,
              },
              searchKey: 'search_query',
              valueKey: 'guid',
              creatable: true,
              createBody: {
                group_id: activeGroup?.id,
                type: ProtocolCollection.entityName,
              },
            },
          };

          const templateField: IFieldProps = {
            fieldType: FieldType.SearchSelect,
            name: 'parent_protocol',
            fieldLabel: 'Template (optional)',
            validation: object().nullable(),
            elementProps: {
              entityName: ProtocolCollection.entityName,
              placeholder: 'Search templates...',
              params: {
                is_template: true,
                group_id: activeGroup?.id,
                has_last_version: true,
              },
              searchKey: 'search_query',
              isClearable: true,
              setOptions: (options) => {
                // Use last_version.id for creation
                options.forEach((o) => {
                  if (o.last_version) {
                    o.value = o.last_version.id;
                  }
                });
                return options;
              },
            },
          };

          return (
            <FormReusable
              autoFocus
              status={status}
              submitButtonText="Create"
              onCancel={toggleModal}
              onSubmit={(values) => {
                const { folder_id, parent_protocol_id } =
                  values as Record<string, any>;
                const body: Record<string, any> = { folder_id };
                if (parent_protocol_id) {
                  body.parent_protocol_id = parent_protocol_id; // id of last_version from template selection
                }
                create(body, {
                  onSuccess: ({ response }) => {
                    if (!response) {
                      return;
                    }
                    history.push(
                      navigation.get('protocol_collection_show', {
                        id: response.result,
                      }),
                    );
                    toggleModal();
                  },
                });
              }}
              fields={[folderField, templateField]}
            />
          );
        }}
      </EntityCanCreateContainer>
    )}
  />
);

export default withRouter(
  withActiveGroup(ProtocolCollectionActionCreateWithFolder),
);
